import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { log, LogLevel } from "utils/LogUtil";
import AgreementsTemplate from "components/templates/Agreements";
import Guide from "components/templates/Agreements/Guide";
import { AGREEMENT_VERSION, GUIDE_VERSION } from "common/constants";
import { updateUser } from "api/userService";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import CommonHeader from "components/organisms/CommonHeader";
import styled from "styled-components";
import { logout, setUser } from "store/slices/userSlice";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: #f7fafd;
`;
const Agreements: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.user);
  const [currentStep, setCurrentStep] = useState<"agreement" | "guide">("agreement");
  const dispatch = useDispatch<AppDispatch>();

  const [updates, setUpdates] = useState<{
    agreementVersion: string;
    guidedVersion: string;
  }>({
    agreementVersion: user?.agreementVersion,
    guidedVersion: user?.guidedVersion,
  });

  // 약관 동의와 가이드 동의가 모두 필요한지 확인
  const needsAgreement = user?.agreementVersion !== AGREEMENT_VERSION;
  const needsGuide = user?.guidedVersion !== GUIDE_VERSION;

  useEffect(() => {
    log(LogLevel.VERBOSE, "Agreements: mounted", { needsAgreement, needsGuide });
    // 둘 다 필요하지 않으면 원래 가려던 페이지로 리다이렉트
    if (!needsAgreement && !needsGuide) {
      const from = location.state?.from || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [needsAgreement, needsGuide, navigate, location.state]);

  const handleAgreementComplete = async () => {
    try {
      let newUpdate = { ...updates, agreementVersion: AGREEMENT_VERSION };
      setUpdates(newUpdate);

      if (needsGuide) {
        setCurrentStep("guide");
      } else {
        await updateUser(user.id, newUpdate);

        dispatch(setUser({ ...user, ...newUpdate }));
        const from = location.state?.from || "/dashboard";
        navigate(from, { replace: true });
      }
    } catch (error) {
      log(LogLevel.ERROR, "약관 동의 처리 실패:", error);
      ToastManager.show({
        content: "동의 처리 중 오류가 발생했습니다.",
        duration: 3000,
      });
    }
  };

  const handleGuideComplete = async () => {
    let newUpdate = { ...updates, guidedVersion: GUIDE_VERSION };
    setUpdates(newUpdate);

    try {
      await updateUser(user.id, newUpdate);
      dispatch(setUser({ ...user, ...newUpdate }));
      const from = location.state?.from || "/dashboard";
      navigate(from, { replace: true });
    } catch (error) {
      log(LogLevel.ERROR, "가이드 동의 처리 실패:", error);
      ToastManager.show({
        content: "동의 처리 중 오류가 발생했습니다.",
        duration: 3000,
      });
    }
  };

  const handleDisagree = async () => {
    await dispatch(logout()).unwrap();
    navigate("/login");
  };

  return (
    <PageContainer>
      <CommonHeader withoutPath={true} logoNavigate={true} />
      {currentStep === "agreement" ? (
        <AgreementsTemplate onAgree={handleAgreementComplete} hasNext={needsGuide} />
      ) : (
        <Guide onAgree={handleGuideComplete} onDisagree={handleDisagree} />
      )}
    </PageContainer>
  );
};

export default Agreements;
